import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  ExternalLink,
  Github,
  Eye,
  Filter,
  Star,
  Calendar,
  ArrowUpRight,
} from "lucide-react";
import { categories } from "../data/mock";
import { useData } from "@/contexts/DataContext";
import DomPurifyContent from "@/utils/domPurifyContent";
import NoDataCard from "@/components/ui/NoDataCard";
// import Spinner from "@/components/ui/Spinner";
import { ProjectCardSkeleton } from "@/components/ui/ProjectCardSkeleton";
import { Skeleton } from "@/components/ui/skeleton";

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [sortBy, setSortBy] = useState("featured");
  const { projects, loading } = useData();

  // if(loading) {
  //   return <Spinner size="60px" />;
  // }
  if (loading) {
    return (
      <div className="container mx-auto py-12">
        {/* Header Skeleton */}
        <div className="text-center mb-16 space-y-4">
          <Skeleton className="h-12 w-64 mx-auto" />
          <Skeleton className="h-6 w-96 mx-auto" />
        </div>

        {/* Featured Skeleton */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <ProjectCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

    if (!projects || projects?.length === 0) {
      return (
        <NoDataCard
          message="No projects found"
          actionLabel="Create Project"
          onAction={() => navigate("/create-project")}
        />
      );
    }

    const filteredProjects = projects?.filter(
      (project) =>
        selectedCategory === "All" || project?.category === selectedCategory
    );

    const sortedProjects = [...filteredProjects]?.sort((a, b) => {
      if (sortBy === "featured") {
        return b.featured - a.featured;
      } else if (sortBy === "newest") {
        return new Date(b?.id) - new Date(a?.id);
      }
      return 0;
    });

    const featuredProjects = projects?.filter((project) => project?.featured);
    const webApps = projects?.filter(
      (project) => project?.category === "Web Application"
    );
    const mobileApps = projects?.filter(
      (project) => project?.category === "Mobile App"
    );
    const systemDesigns = projects?.filter(
      (project) => project?.category === "System Design"
    );
    const devOpsProjects = projects?.filter(
      (project) => project?.category === "DevOps"
    );
    const scrapingProjects = projects?.filter(
      (project) => project?.category === "Data Scraping"
    );
    const htmlProjects = projects?.filter(
      (project) => project?.category === "HTML Template For eBay"
    );
    const npmProject = projects?.filter(
      (project) => project?.category === "Npm Packages"
    );
    const dataEngineeringProject = projects?.filter(
      (project) => project?.category === "Data Engineering"
    );

    const getStatusColor = (status) => {
      switch (status) {
        case "Completed":
          return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300";
        case "In Progress":
          return "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300";
        default:
          return "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300";
      }
    };

    const ProjectCard = ({ project, featured = false }) => (
      <Card
        className={`group hover:shadow-xl transition-all duration-300 overflow-hidden ${featured ? "lg:col-span-2" : ""
          }`}
      >
        <div
          className={`aspect-video overflow-hidden ${featured ? "lg:aspect-[21/9]" : ""
            }`}
        >
          <img
            src={project?.images[0]?.url}
            alt={project.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute top-4 left-4 flex gap-2">
            {project.featured && (
              <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300">
                <Star className="w-3 h-3 mr-1" />
                Featured
              </Badge>
            )}
            <Badge className={getStatusColor(project.status)}>
              {project.status}
            </Badge>
          </div>
        </div>

        <CardContent className="p-6">
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Badge variant="outline" className="text-xs">
                  {project.category}
                </Badge>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Eye className="w-3 h-3" />
                  <span>{Math.floor(Math.random() * 500) + 100}</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2 group-hover:text-purple-600 transition-colors">
                {project.title}
              </h3>
              <DomPurifyContent
                className="text-muted-foreground text-sm line-clamp-2"
                content={project?.description}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {project.technologies
                .slice(0, featured ? 5 : 3)
                .map((tech, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {tech}
                  </Badge>
                ))}
              {project?.technologies?.length > (featured ? 5 : 3) && (
                <Badge variant="secondary" className="text-xs">
                  +{project?.technologies?.length - (featured ? 5 : 3)} more
                </Badge>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex space-x-2">
                {project?.liveUrl && (
                  <Button size="sm" variant="ghost" asChild>
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Live
                    </a>
                  </Button>
                )}
                <Button size="sm" variant="ghost" asChild>
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Github className="h-4 w-4 mr-1" />
                    Code
                  </a>
                </Button>
              </div>
              <Button
                size="sm"
                className="bg-purple-600 hover:bg-purple-700"
                asChild
              >
                <Link to={`/portfolio/${project?._id}`}>
                  View Details
                  <ArrowUpRight className="h-3 w-3 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );

    return (
      <div className="min-h-screen py-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              My{" "}
              <span className="bg-gradient-to-r from-purple-600 to-black bg-clip-text text-transparent">
                Portfolio
              </span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A showcase of my projects spanning web applications, mobile apps,
              system design, and DevOps solutions.
            </p>
          </div>

          {/* Portfolio Tabs */}
          <Tabs defaultValue="all" className="w-full">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 gap-4">
              <TabsList className="grid w-full lg:w-auto grid-cols-2 lg:grid-cols-5 h-auto">
                <TabsTrigger
                  value="all"
                  className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                >
                  All ({projects?.length})
                </TabsTrigger>
                <TabsTrigger
                  value="featured"
                  className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                >
                  Featured ({featuredProjects?.length})
                </TabsTrigger>
                <TabsTrigger
                  value="web"
                  className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                >
                  Web Apps ({webApps?.length})
                </TabsTrigger>
                <TabsTrigger
                  value="mobile"
                  className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                >
                  Mobile ({mobileApps?.length})
                </TabsTrigger>
                <TabsTrigger
                  value="system"
                  className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                >
                  System ({systemDesigns?.length + devOpsProjects.length})
                </TabsTrigger>
                <TabsTrigger
                  value="htmlProjects"
                  className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                >
                  HTML ({htmlProjects?.length})
                </TabsTrigger>
                <TabsTrigger
                  value="scraping"
                  className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                >
                  Data Scraping ({scrapingProjects?.length})
                </TabsTrigger>
                <TabsTrigger
                  value="npmProject"
                  className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                >
                  Npm Packages ({npmProject?.length})
                </TabsTrigger>
                <TabsTrigger
                  value="dataEngineeringProject"
                  className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
                >
                  Data Engineering ({dataEngineeringProject?.length})
                </TabsTrigger>
              </TabsList>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Filter className="w-4 h-4" />
                  <span>Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="bg-background border border-border rounded px-2 py-1 text-sm"
                  >
                    <option value="featured">Featured First</option>
                    <option value="newest">Newest First</option>
                  </select>
                </div>
              </div>
            </div>

            {/* All Projects */}
            <TabsContent value="all" className="mt-8">
              <div className="grid lg:grid-cols-3 gap-8">
                {sortedProjects?.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            </TabsContent>

            {/* Featured Projects */}
            <TabsContent value="featured" className="mt-8">
              <div className="grid lg:grid-cols-3 gap-8">
                {featuredProjects?.map((project, index) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    featured={index === 0}
                  />
                ))}
              </div>
            </TabsContent>

            {/* Web Applications */}
            <TabsContent value="web" className="mt-8">
              <div className="grid lg:grid-cols-3 gap-8">
                {webApps?.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            </TabsContent>

            {/* Mobile Apps */}
            <TabsContent value="mobile" className="mt-8">
              <div className="grid lg:grid-cols-3 gap-8">
                {mobileApps?.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            </TabsContent>
            <TabsContent value="htmlProject" className="mt-8">
              <div className="grid lg:grid-cols-3 gap-8">
                {htmlProjects?.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            </TabsContent>
            <TabsContent value="scraping" className="mt-8">
              <div className="grid lg:grid-cols-3 gap-8">
                {scrapingProjects?.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            </TabsContent>
            <TabsContent value="npmProject" className="mt-8">
              <div className="grid lg:grid-cols-3 gap-8">
                {npmProject?.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            </TabsContent>
            <TabsContent value="dataEngineeringProject" className="mt-8">
              <div className="grid lg:grid-cols-3 gap-8">
                {dataEngineeringProject?.map((project) => (
                  <ProjectCard key={project.id} project={project} />
                ))}
              </div>
            </TabsContent>

            {/* System Design & DevOps */}
            <TabsContent value="system" className="mt-8">
              <div className="space-y-12">
                {/* System Design Projects */}
                <div>
                  <h3 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                    <Badge variant="outline">System Design</Badge>
                    <span className="text-sm text-muted-foreground">
                      ({systemDesigns.length})
                    </span>
                  </h3>
                  <div className="grid lg:grid-cols-3 gap-8">
                    {systemDesigns?.map((project) => (
                      <ProjectCard key={project.id} project={project} />
                    ))}
                  </div>
                </div>

                {/* DevOps Projects */}
                <div>
                  <h3 className="text-2xl font-semibold mb-6 flex items-center gap-2">
                    <Badge variant="outline">DevOps</Badge>
                    <span className="text-sm text-muted-foreground">
                      ({devOpsProjects.length})
                    </span>
                  </h3>
                  <div className="grid lg:grid-cols-3 gap-8">
                    {devOpsProjects?.map((project) => (
                      <ProjectCard key={project.id} project={project} />
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Stats Section */}
          <div className="mt-20 py-16 bg-gradient-to-r from-purple-50 to-black/5 dark:from-purple-950/20 dark:to-black/40 rounded-3xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Project Statistics</h2>
              <p className="text-muted-foreground">
                Summary of my development work and achievements
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {projects?.length}
                </div>
                <div className="text-sm text-muted-foreground">
                  Total Projects
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {featuredProjects?.length}
                </div>
                <div className="text-sm text-muted-foreground">
                  Featured Projects
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {projects?.filter((p) => p.liveUrl).length}
                </div>
                <div className="text-sm text-muted-foreground">
                  Live Deployments
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {[...new Set(projects?.flatMap((p) => p.technologies))].length}
                </div>
                <div className="text-sm text-muted-foreground">
                  Technologies Used
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-20 text-center">
            <h2 className="text-3xl font-bold mb-4">Interested in My Work?</h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              I'm always open to discussing new projects and opportunities. Let's
              create something amazing together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                asChild
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Link to="/contact">
                  Start a Project
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/about">
                  Learn More About Me
                  <Calendar className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  export default Portfolio;
